import { ChevronLeft, ChevronRight, ListChecks, Maximize2, Minimize2, Pencil } from 'lucide-react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import DrawingBoard, { type DrawingSheet } from '../../../components/drawing/DrawingBoard';
import { BookletTimer } from './ExamBookletViewer';

type BranchKey = 'sosyal_bilimler' | 'sosyal_bilimler2';
type BranchSelections = Partial<Record<BranchKey, 'din' | 'felsefe'>>;
type Answers = Record<string, (string | null)[]>;

interface TestDef {
  key: string;
  label: string;
  count: number;
  commonCount?: number; // Din / İlave Felsefe seçiminden önceki ortak soru sayısı
  extra?: number; // İlave Felsefe soruları kitapta Din'den bu kadar sonra başlar
}

type SectionDef = { key: string; label: string; durationMinutes: number; tests: TestDef[] };

const YKS_OPTIONS = ['A', 'B', 'C', 'D', 'E'];
const LGS_OPTIONS = ['A', 'B', 'C', 'D'];

const YKS_SECTIONS: SectionDef[] = [
  {
    key: 'tyt',
    label: 'TYT',
    durationMinutes: 165,
    tests: [
      { key: 'turkce', label: 'Türkçe', count: 40 },
      { key: 'sosyal_bilimler', label: 'Sosyal Bilimler', count: 20, commonCount: 15, extra: 5 },
      { key: 'temel_matematik', label: 'Temel Matematik', count: 40 },
      { key: 'fen_bilimleri', label: 'Fen Bilimleri', count: 20 },
    ],
  },
  {
    key: 'ayt',
    label: 'AYT',
    durationMinutes: 180,
    tests: [
      { key: 'edebiyat_sosyal1', label: 'Edebiyat-Sosyal 1', count: 40 },
      { key: 'sosyal_bilimler2', label: 'Sosyal Bilimler 2', count: 40, commonCount: 34, extra: 6 },
      { key: 'matematik', label: 'Matematik', count: 40 },
      { key: 'fen_bilimleri_ayt', label: 'Fen Bilimleri', count: 40 },
    ],
  },
];

// LGS: iki oturum, 4 şık, her testte numara 1'den başlar. Test anahtarları sunucudaki LGS düzeniyle aynı (internal_exam_layouts).
const LGS_SECTIONS: SectionDef[] = [
  {
    key: 'sozel',
    label: 'Sözel Bölüm',
    durationMinutes: 75,
    tests: [
      { key: 'turkce', label: 'Türkçe', count: 20 },
      { key: 'sosyal', label: 'T.C. İnkılap Tarihi', count: 10 },
      { key: 'din', label: 'Din Kültürü', count: 10 },
      { key: 'ingilizce', label: 'İngilizce', count: 10 },
    ],
  },
  {
    key: 'sayisal',
    label: 'Sayısal Bölüm',
    durationMinutes: 80,
    tests: [
      { key: 'matematik', label: 'Matematik', count: 20 },
      { key: 'fen', label: 'Fen Bilimleri', count: 20 },
    ],
  },
];

// Görseller aynı çözünürlükte kesildi: sabit ölçekle her soruda yazı aynı boyda kalır, kısa soru büyümez
export const IMAGE_SCALE = 0.72;
// Tam ekranda uzun soru kutuya sığdırılır; bunun altında yazı okunmaz, o sorular kutunun içinde kayar
const MIN_FIT_SCALE = 0.5;
// Kartta sorudan kalan pay: başlık, kalem çubuğu, A-E, kenar boşluğu. Kalem kapalıyken de ayrılır, açılınca soru ölçek değiştirmez
const CARD_CHROME_HEIGHT = 192;

const normalizeAnswers = (tests: TestDef[], seed?: Answers | null): Answers =>
  Object.fromEntries(tests.map((test) => [
    test.key,
    Array.from({ length: test.count }, (_, index) => {
      const value = seed?.[test.key]?.[index];
      return typeof value === 'string' && value.trim() ? value : null;
    }),
  ]));

const countFilled = (answers: Answers) => Object.values(answers).reduce((sum, list) => sum + list.filter(Boolean).length, 0);

// 3 saatlik oturumda sayfa yenilenir, telefon sekmeyi kapatır: her işaret tarayıcıda da tutulur
const draftKey = (examId: string) => `examQuestionDraft:${examId}`;

const readDraft = (examId: string): { answers?: Answers; branchSelections?: BranchSelections } | null => {
  try {
    return JSON.parse(window.localStorage.getItem(draftKey(examId)) || 'null');
  } catch {
    return null;
  }
};

const writeDraft = (examId: string, answers: Answers, branchSelections: BranchSelections) => {
  try {
    window.localStorage.setItem(draftKey(examId), JSON.stringify({ answers, branchSelections }));
  } catch {
    // Gizli sekme vb.: yalnız sunucuya kaydedilen cevaplar kalır
  }
};

// Kitaptaki soru numarası: kâğıttan çözen öğrenci optikte aynı numarayı arar. İlave Felsefe, Din'den sonra basılıdır (21-25, 41-46).
const printedNumber = (test: TestDef, slot: number, branch?: 'din' | 'felsefe') => {
  const isExtra = test.commonCount !== undefined && slot >= test.commonCount && branch === 'felsefe';
  return slot + 1 + (isExtra ? test.extra || 0 : 0);
};

const questionUrl = (baseUrl: string, test: TestDef, slot: number, branch?: 'din' | 'felsefe') =>
  `${baseUrl}${String(printedNumber(test, slot, branch)).padStart(2, '0')}.png`;

type Props = {
  examId: string;
  levelType?: string;
  questionImages: Record<string, string>;
  availableSegments?: string[];
  initialAnswers?: Answers;
  initialBranchSelections?: BranchSelections;
  submitting?: boolean;
  submitLabel?: string;
  sourceNote?: string;
  onSubmit: (payload: { kitapcikTuru: 'A'; answers: Answers; branchSelections: BranchSelections }) => void | Promise<void>;
};

export default function ExamQuestionSolver({
  examId,
  levelType,
  questionImages,
  availableSegments,
  initialAnswers,
  initialBranchSelections,
  submitting = false,
  submitLabel = 'Sınavı Kaydet',
  sourceNote,
  onSubmit,
}: Props) {
  const isLgs = levelType === 'lgs';
  const OPTIONS = isLgs ? LGS_OPTIONS : YKS_OPTIONS;
  const sections = useMemo(
    () => (isLgs ? LGS_SECTIONS : YKS_SECTIONS).filter((section) => !availableSegments?.length || availableSegments.includes(section.key)),
    [availableSegments, isLgs]
  );
  const [start] = useState(() => {
    const allTests = (isLgs ? LGS_SECTIONS : YKS_SECTIONS).flatMap((section) => section.tests);
    const saved = normalizeAnswers(allTests, initialAnswers);
    const draft = readDraft(examId);
    const local = normalizeAnswers(allTests, draft?.answers);
    // Başka cihazda kaydedilen daha dolu cevabı eski taslak ezmez
    const useLocal = Boolean(draft) && countFilled(local) >= countFilled(saved);
    return {
      answers: useLocal ? local : saved,
      branches: {
        sosyal_bilimler: (useLocal ? draft?.branchSelections : initialBranchSelections)?.sosyal_bilimler === 'felsefe' ? 'felsefe' : 'din',
        sosyal_bilimler2: (useLocal ? draft?.branchSelections : initialBranchSelections)?.sosyal_bilimler2 === 'felsefe' ? 'felsefe' : 'din',
      } as BranchSelections,
    };
  });
  const [answers, setAnswers] = useState<Answers>(start.answers);
  const [branches, setBranches] = useState<BranchSelections>(start.branches);
  const [sectionKey, setSectionKey] = useState(sections[0]?.key);
  const [testKey, setTestKey] = useState(sections[0]?.tests[0].key);
  const [slot, setSlot] = useState(0);
  // Yeni soru hazır olana kadar eskisi ekranda kalır: görsel doğal boyda açılıp küçülmez, alan boşalmaz
  const [shown, setShown] = useState<{ url: string; width: number; height: number } | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [cardHeight, setCardHeight] = useState<number | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [toolbarHost, setToolbarHost] = useState<HTMLDivElement | null>(null);
  // Çizimler sayfa açık kaldıkça durur; tarayıcı deposuna yazılmaz, cevap taslağının yerini doldurmasın
  const sheets = useRef(new Map<string, DrawingSheet>());
  const [host] = useState(() => document.createElement('div'));
  const slotRef = useRef<HTMLDivElement | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const section = sections.find((item) => item.key === sectionKey) || sections[0];
  const test = section.tests.find((item) => item.key === testKey) || section.tests[0];
  const baseUrl = questionImages[test.key] || '';
  const branch = branches[test.key as BranchKey];
  const isBranchQuestion = test.commonCount !== undefined && slot >= test.commonCount;
  const url = questionUrl(baseUrl, test, slot, branch);
  const filledAll = countFilled(answers);
  const totalAll = sections.reduce((sum, item) => sum + item.tests.reduce((inner, t) => inner + t.count, 0), 0);

  useEffect(() => {
    writeDraft(examId, answers, branches);
  }, [answers, branches, examId]);

  useEffect(() => {
    if (!baseUrl) return undefined;
    let cancelled = false;
    setFailed(false);
    const image = new Image();
    image.onload = () => {
      if (!cancelled) setShown({ url, width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      if (!cancelled) setFailed(true);
    };
    image.src = url;
    // Sonraki soru beklemeden açılsın
    if (slot + 1 < test.count) new Image().src = questionUrl(baseUrl, test, slot + 1, branch);
    return () => {
      cancelled = true;
    };
  }, [url, attempt]);

  // Yeni soru hep baştan görünür: önceki sorunun kaydırması yeni sorunun üstünü gizlemesin
  useEffect(() => {
    boxRef.current?.scrollTo({ top: 0 });
    const card = cardRef.current;
    let scroller = card?.parentElement;
    while (scroller && !/auto|scroll/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
    if (card && scroller && card.getBoundingClientRect().top < scroller.getBoundingClientRect().top) card.scrollIntoView();
  }, [shown?.url]);

  // Tam ekranda geniş ekranın düzeni sabittir, kart yüksekliği sorudan bağımsızdır
  useEffect(() => {
    const card = cardRef.current;
    if (!card || !fullscreen) {
      setCardHeight(null);
      return undefined;
    }
    const measure = () => setCardHeight(window.matchMedia('(min-width: 1024px)').matches ? card.clientHeight : null);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(card);
    return () => observer.disconnect();
  }, [fullscreen]);

  // Sayfa kapsayıcısı fixed'i içeride tutuyor: tam ekranda ekran body'ye taşınır. Düğüm taşındığı için React ağacı
  // yeniden kurulmaz, süre ve çizim kaybolmaz.
  useLayoutEffect(() => {
    host.className = fullscreen ? 'fixed inset-0 z-[1000] overflow-auto bg-gray-50 lg:overflow-hidden' : '';
    (fullscreen ? document.body : slotRef.current)?.appendChild(host);
  }, [fullscreen, host]);

  useEffect(() => () => host.remove(), [host]);

  // Uygulama başlığı ve menü kaplanır (iOS'ta tarayıcı tam ekranı yok); olan cihazda tarayıcı çubukları da kalkar
  useEffect(() => {
    if (!fullscreen) return undefined;
    document.documentElement.requestFullscreen?.()?.catch(() => undefined);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFullscreen(false);
    };
    const onChange = () => {
      if (!document.fullscreenElement) setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('fullscreenchange', onChange);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onChange);
      if (document.fullscreenElement) document.exitFullscreen().catch(() => undefined);
    };
  }, [fullscreen]);

  const goTo = useCallback((nextTest: TestDef, nextSlot: number) => {
    setTestKey(nextTest.key);
    setSlot(nextSlot);
  }, []);

  const move = (step: number) => {
    const next = slot + step;
    if (next >= 0 && next < test.count) {
      setSlot(next);
      return;
    }
    const index = section.tests.findIndex((item) => item.key === test.key) + step;
    const neighbor = section.tests[index];
    if (neighbor) goTo(neighbor, step > 0 ? 0 : neighbor.count - 1);
  };

  const pick = (option: string, index = slot) => {
    setAnswers((current) => {
      const list = [...current[test.key]];
      list[index] = list[index] === option ? null : option;
      return { ...current, [test.key]: list };
    });
  };

  const submit = () => {
    setFullscreen(false); // Onay penceresi tam ekranın altında kalmasın
    onSubmit({ kitapcikTuru: 'A', answers, branchSelections: branches });
  };

  const changeBranch = (next: 'din' | 'felsefe') => {
    if (next === branch) return;
    setBranches((current) => ({ ...current, [test.key]: next }));
    // Bu kutulardaki işaretler diğer dersin sorularına aitti
    setAnswers((current) => ({
      ...current,
      [test.key]: current[test.key].map((value, index) => (index >= (test.commonCount || 0) ? null : value)),
    }));
  };

  const atStart = slot === 0 && section.tests[0].key === test.key;
  const atEnd = slot === test.count - 1 && section.tests[section.tests.length - 1].key === test.key;
  const selected = answers[test.key][slot];
  const fittedScale = cardHeight && shown ? (cardHeight - CARD_CHROME_HEIGHT) / shown.height : IMAGE_SCALE;
  const scale = Math.min(IMAGE_SCALE, Math.max(MIN_FIT_SCALE, fittedScale));

  const screen = (
    <div className={`flex flex-col gap-3 ${fullscreen ? 'p-3 sm:p-4 lg:h-full' : ''}`}>
      <div className="flex flex-wrap items-center gap-2">
        {sections.length > 1 && sections.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => {
              setSectionKey(item.key);
              goTo(item.tests[0], 0);
            }}
            className={`rounded-lg px-4 py-2 text-sm font-bold ${item.key === section.key ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {item.label}
          </button>
        ))}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setFullscreen((current) => !current)}
            aria-pressed={fullscreen}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50"
          >
            {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            {fullscreen ? 'Tam ekrandan çık' : 'Tam ekran'}
          </button>
          <BookletTimer examId={examId} booklet={{ key: section.key, durationMinutes: section.durationMinutes }} />
          <button
            type="button"
            onClick={submit}
            disabled={!filledAll || submitting}
            className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? 'Gönderiliyor...' : submitLabel}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4">
        {section.tests.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => goTo(item, 0)}
            className={`min-w-0 rounded-lg border px-2 py-1.5 text-left text-xs font-semibold ${item.key === test.key ? 'border-blue-600 bg-blue-50 text-blue-800' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}
          >
            <span className="block truncate">{item.label}</span>
            <span className="font-normal text-gray-500">{answers[item.key].filter(Boolean).length}/{item.count}</span>
          </button>
        ))}
      </div>

      <div className={`lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-4 ${fullscreen ? 'lg:min-h-0 lg:flex-1 lg:grid-rows-[minmax(0,1fr)]' : 'lg:items-start'}`}>
        <div ref={cardRef} className={`min-w-0 rounded-xl border border-gray-200 bg-white ${fullscreen ? 'lg:flex lg:min-h-0 lg:flex-col' : ''}`}>
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-3 py-2">
            <span className="text-sm font-bold text-gray-900">
              {bulk ? `${test.label} · Toplu cevap` : `${test.label} · Soru ${printedNumber(test, slot, branch)} / ${printedNumber(test, test.count - 1, branch)}`}
            </span>
            <div className="flex items-center gap-2">
              {(bulk ? test.commonCount !== undefined : isBranchQuestion) && (
                <span className="inline-flex rounded-lg bg-gray-100 p-0.5">
                  {(['din', 'felsefe'] as const).map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => changeBranch(item)}
                      className={`rounded-md px-2.5 py-1 text-xs font-bold ${branch === item ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500'}`}
                    >
                      {item === 'din' ? 'Din' : 'İlave Felsefe'}
                    </button>
                  ))}
                </span>
              )}
              {bulk ? (
                <button
                  type="button"
                  onClick={() => setBulk(false)}
                  className="rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50"
                >
                  Soru soru çöz
                </button>
              ) : !drawing && (
                <button
                  type="button"
                  onClick={() => setDrawing(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50"
                >
                  <Pencil size={14} />
                  Kalem
                </button>
              )}
            </div>
          </div>

          {bulk ? (
            <div className={fullscreen ? 'lg:min-h-0 lg:flex-1 lg:overflow-y-auto' : ''}>
            <div className="columns-1 gap-x-4 p-3 sm:columns-2 xl:columns-3">
              {Array.from({ length: test.count }, (_, index) => (
                <React.Fragment key={index}>
                  {index === test.commonCount && (
                    <p className="mb-1 mt-2 break-after-avoid text-xs font-bold text-blue-700">{branch === 'din' ? 'Din Kültürü' : 'İlave Felsefe'}</p>
                  )}
                  <div className="mb-1 flex break-inside-avoid items-center gap-1">
                    <span className="w-6 shrink-0 text-right text-xs font-semibold text-gray-500">{printedNumber(test, index, branch)}</span>
                    {OPTIONS.map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => pick(option, index)}
                        aria-label={`${printedNumber(test, index, branch)}. soru ${option}`}
                        aria-pressed={answers[test.key][index] === option}
                        className={`h-9 w-9 shrink-0 rounded-full border text-xs font-bold transition sm:h-8 sm:w-8 ${answers[test.key][index] === option ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-600 hover:border-blue-400'}`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </React.Fragment>
              ))}
            </div>
            </div>
          ) : (
            <>
              <div ref={setToolbarHost} />
              <div ref={boxRef} className={`overflow-x-auto ${fullscreen ? 'lg:min-h-0 lg:flex-1 lg:overflow-y-auto' : ''}`}>
                <div className={`relative min-h-[18rem] p-3 ${drawing ? 'pb-40' : ''} ${fullscreen ? 'lg:min-h-full lg:pb-3' : ''}`}>
                  {!baseUrl || failed ? (
                    <div className="py-12 text-center text-sm text-gray-500">
                      Soru açılamadı.
                      <button type="button" onClick={() => setAttempt((current) => current + 1)} className="ml-2 font-bold text-blue-700">Yeniden dene</button>
                    </div>
                  ) : shown && (
                    <img
                      src={shown.url}
                      alt={`${test.label} ${printedNumber(test, slot, branch)}. soru`}
                      style={{ width: shown.width * scale, maxWidth: '100%' }}
                      className={`mx-auto block h-auto ${shown.url === url ? '' : 'opacity-40'}`}
                    />
                  )}
                  {/* Çizim soruyla birlikte kayar; şıklar bu katmanın dışında, kalem açıkken de işaretlenir */}
                  {drawing && (
                    <div className="absolute inset-0 z-10">
                      <DrawingBoard
                        className="h-full w-full"
                        onClose={() => setDrawing(false)}
                        toolbarHost={toolbarHost}
                        sheets={sheets.current}
                        sheetKey={shown?.url}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="sticky bottom-0 z-20 flex items-center gap-2 rounded-b-xl border-t border-gray-100 bg-white p-2">
                <button type="button" onClick={() => move(-1)} disabled={atStart} aria-label="Önceki soru" className="shrink-0 rounded-lg border border-gray-200 p-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                  <ChevronLeft size={20} />
                </button>
                <div className="flex min-w-0 flex-1 justify-center gap-1 sm:gap-1.5">
                  {OPTIONS.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => pick(option)}
                      aria-pressed={selected === option}
                      className={`h-10 w-10 shrink-0 rounded-full border-2 text-sm font-bold transition sm:h-11 sm:w-11 ${selected === option ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400'}`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                <button type="button" onClick={() => move(1)} disabled={atEnd} aria-label="Sonraki soru" className="shrink-0 rounded-lg border border-gray-200 p-2 text-gray-700 hover:bg-gray-50 disabled:opacity-40">
                  <ChevronRight size={20} />
                </button>
              </div>
            </>
          )}
        </div>

        <aside className={`mt-3 rounded-xl border border-gray-200 bg-white p-3 lg:sticky lg:top-0 lg:mt-0 ${fullscreen ? 'lg:max-h-full lg:self-start lg:overflow-y-auto' : ''}`}>
          {!bulk && (
            <>
              <button
                type="button"
                onClick={() => setBulk(true)}
                className="mb-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-2 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50"
              >
                <ListChecks size={14} />
                Toplu ekle (optik)
              </button>
              <div className="grid grid-cols-10 gap-1 lg:grid-cols-5">
                {Array.from({ length: test.count }, (_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSlot(index)}
                    aria-label={`${printedNumber(test, index, branch)}. soru`}
                    className={`rounded py-1.5 text-xs font-semibold ${index === slot ? 'ring-2 ring-blue-600 ring-offset-1' : ''} ${answers[test.key][index] ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {printedNumber(test, index, branch)}
                  </button>
                ))}
              </div>
            </>
          )}
          <p className={`${bulk ? '' : 'mt-3 '}text-xs text-gray-500`}>Toplam: <strong className="text-gray-900">{filledAll}</strong> / {totalAll} soru işaretlendi</p>
          {sourceNote && <p className="mt-2 text-[11px] leading-4 text-gray-400">{sourceNote}</p>}
        </aside>
      </div>
    </div>
  );

  return <div ref={slotRef}>{createPortal(screen, host)}</div>;
}
